import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { sendProviderClickEvent } from "src/actions/Analytics";
import { GetErrorPageUrl } from "src/actions/GetErrorPageUrl";
import { AuthContext } from "src/contexts/AuthContext";
import { ThemeContext } from "src/contexts/ThemeContext";
import SelectProvider from "../../actions/SelectProvider";
import { setGloboIdFromCookie } from "../../actions/SetGloboIdFromCookie";
import { Loading, ProviderButton, SearchBar } from "../../components";
import { SearchProviderContext } from "../../contexts/SearchProviderContext";
import { sendEvent } from "../../hooks/analytics";
import { getProviderByProviderID } from "../../hooks/integrator";
import { getProvidersByService } from "../../hooks/be";
import { useQueryParams } from "../../hooks/queryParams";
import { Provider } from "../../types";
import {
  ContainerListProvider,
  Description,
  Grid,
  LoadMoreButton,
  Title
} from "./styles";
import { getSafeCallbackUrl } from "src/utils/security";

type Params = {
  serviceId: string;
};

type ProvidersListProps = {
  onSelectProvider: Function;
};

enum TextLabel {
  OCULTAR = "Ocultar",
  EXIBIR = "Ver mais"
}

const ProvidersList = ({ onSelectProvider }: ProvidersListProps) => {
  const initialListSize = 6;

  const { filteredProviders } = useContext(SearchProviderContext);

  const [visibleProviders, setVisibleProviders] = useState(initialListSize);
  const [expanded, setExpanded] = useState(false);
  const itemRef = useRef<HTMLButtonElement | null>(null);

  const handleVisibleProviders = () => {
    setExpanded(!expanded);
    setVisibleProviders(prev =>
      prev === initialListSize ? filteredProviders.length : initialListSize
    );
  };

  const providersGrid = filteredProviders
    .slice(0, visibleProviders)
    .map((provider, index) => (
      <ProviderButton
        ref={index === initialListSize ? itemRef : null}
        key={index}
        provider={provider}
        onClick={() => onSelectProvider(provider)}
      />
    ));

  useEffect(() => {
    if (expanded) {
      setTimeout(() => {
        itemRef.current?.focus();
      }, 0);
    }
  }, [expanded]);

  return (
    <ContainerListProvider>
      <Title tabIndex={-1}>Selecione a sua operadora</Title>
      <Description tabIndex={-1}>
        Selecione a sua operadora na lista para associar à sua Conta Globo.
      </Description>
      <SearchBar tabIndex={0} />
      {filteredProviders.length > 0 && (
        <Grid tabIndex={0} title="Lista de operadoras: selecione sua operadora">
          {providersGrid}
        </Grid>
      )}
      <LoadMoreButton tabIndex={0} onClick={handleVisibleProviders}>
        {expanded ? TextLabel.OCULTAR : TextLabel.EXIBIR}
      </LoadMoreButton>
    </ContainerListProvider>
  );
};

const ProviderSelection = () => {
  setGloboIdFromCookie();
  const providerSelectionLabel = "Associar Operadora";

  const { serviceId } = useParams<Params>() as Params;
  const globoId = localStorage.getItem("globoId") || "";
  const queryParams = useQueryParams();
  const referrer = document.referrer;

  const callbackUrl = getSafeCallbackUrl(queryParams.get("url"));

  const originProvider = queryParams.get("originProvider");
  const navigate = useNavigate();

  localStorage.setItem(
    "alfUrlInit",
    `${window.location.pathname}?${queryParams.toString()}`
  );

  const authentication = useContext(AuthContext);
  useEffect(() => {
    sendEvent(
      "alf_page_view",
      {
        user_code_provider_hit: "cadun",
        page_name_custom: "escolha_sua_operadora",
        provider_name: "",
        referer: referrer
      },
      { user_code: globoId, user_code_provider: "cadun" }
    );

    authentication.authenticate();
  }, []);

  const { providers, setProviders } = useContext(SearchProviderContext);
  const { setHeaderReturn } = useContext(ThemeContext);
  setHeaderReturn(false);

  useEffect(() => {
    if (originProvider) {
      getProviderByProviderID(originProvider)
        .then(provider => {
          sendProviderClickEvent(
            providerSelectionLabel,
            globoId,
            provider.name
          );

          setTimeout(() => {
            SelectProvider(provider, serviceId, callbackUrl);
          }, 1000);
        })
        .catch(() => {
          navigate(GetErrorPageUrl());
        });

      return;
    }

    getProvidersByService(serviceId).then(providers => {
      setProviders(providers);
    });
  }, []);

  const handleProviderSelection = (provider: Provider) => {
    sendProviderClickEvent(providerSelectionLabel, globoId, provider.name);
    SelectProvider(provider, serviceId, callbackUrl);
  };

  if (!authentication.user || providers.length === 0) {
    return <Loading />;
  }

  return <ProvidersList onSelectProvider={handleProviderSelection} />;
};

export default ProviderSelection;
